import type { HomePlanSchemaMove } from "api-types/homePlans/homePlanRequests";
import type {
  ClientHomePlan,
  ClientHomePlanHydrated,
  ClientHomePlanHydratedNoDoc,
  ClientHomePlanStatusUpdateResponse,
  HomePlanLockedAssets,
} from "api-types/homePlans/homePlanResponses";
import type { HomePlanCategory, HomePlanStatus } from "api-types/homePlans/homePlanTypes";
import type { ClientMailResponse } from "api-types/mail/mailResponses";
import { LAMDA_REQUEST_TIMEOUT, WEB_API_BASE_PATH } from "common/constants";
import type { UserNotificationType } from "common/types/userNotification";
import downloadFile from "common/utils/downloadFile";
import { handleFetchError } from "common/utils/fetchHelpers";
import type { PublicPlanType } from "lib/requestTypes";
import {
  deleteWithTimeout,
  getWithTimeout,
  postWithTimeout,
  postWithTimeoutNoAuth,
  putWithTimeout,
} from "lib/utils/fetchAuthHelpers";
import type { StorageResponse } from "./types";

export async function getHomePlanHydrated(
  builderId: string,
  homePlanId: string,
): StorageResponse<ClientHomePlanHydrated> {
  try {
    const res = await getWithTimeout(`${WEB_API_BASE_PATH}/builders/${builderId}/home-plans/${homePlanId}`);
    const json = await res.json();
    if (!res.ok) {
      throw new Error(json);
    }
    return [json, undefined];
  } catch (error) {
    console.error(error);
    return [, await handleFetchError(error)];
  }
}

export interface HomePlanDiscountFields {
  amount?: number;
  description?: string;
}

export type PostHomePlanFields = Partial<
  Pick<
    ClientHomePlan,
    | "communityPlanId"
    | "externalId"
    | "planName"
    | "planStatus"
    | "optionChoices"
    | "lotId"
    | "communityPlanId"
    | "salesStaffId"
    | "notes"
    | "customerInfo"
    | "customerCustomFields"
    | "category"
    | "source"
    | "totalPriceOverride"
  > & {
    discount: HomePlanDiscountFields;
  }
>;

// TODO: Revert to default timeout once home plan creation is optimized.
// Currently increased to 20s to account for long running home plan creation with many options
const HOME_PLAN_POST_TIMEOUT = 20000;

export async function postHomePlan(
  builderId: string,
  {
    communityPlanId,
    externalId,
    lotId,
    salesStaffId,
    optionChoices,
    notes,
    planName,
    customerInfo,
    category,
    source,
    discount,
    totalPriceOverride,
  }: PostHomePlanFields,
): StorageResponse<ClientHomePlan> {
  try {
    const res = await postWithTimeout(
      `${WEB_API_BASE_PATH}/builders/${builderId}/home-plans`,
      {
        optionChoices,
        communityPlanId,
        externalId,
        salesStaffId,
        lotId,
        notes,
        planName,
        customerInfo,
        category,
        source,
        discount,
        totalPriceOverride,
      },
      HOME_PLAN_POST_TIMEOUT,
    );

    const json = await res.json();

    if (!res.ok) {
      throw json;
    }

    return [json as ClientHomePlan, undefined];
  } catch (e) {
    return [, await handleFetchError(e)];
  }
}

export async function postPublicHomePlan(
  builderId: string,
  planType: PublicPlanType,
  planId: string,
  homePlan: Omit<PostHomePlanFields, "communityPlanId" | "discount">,
): StorageResponse<ClientHomePlan> {
  try {
    const res = await postWithTimeoutNoAuth(
      `${WEB_API_BASE_PATH}/builders/${builderId}/public-plans`,
      {
        planType,
        planId,
        homePlan,
      },
      HOME_PLAN_POST_TIMEOUT,
    );

    const json = await res.json();

    if (!res.ok) {
      throw json;
    }

    return [json as ClientHomePlan, undefined];
  } catch (e) {
    return [, await handleFetchError(e)];
  }
}

export async function shareHomePlan(
  builderId: string,
  homePlanId: string,
  emails: string[],
): StorageResponse<{ delivered: boolean }> {
  try {
    const res = await postWithTimeout(`${WEB_API_BASE_PATH}/mail/share-plan`, {
      builderId,
      homePlanId,
      emails,
    });

    const json = (await res.json()) as ClientMailResponse;

    if (!res.ok) {
      throw json;
    }

    return [{ delivered: json.delivered }, undefined];
  } catch (e) {
    return [, await handleFetchError(e)];
  }
}

export const HOME_PLAN_CATEGORY_DEFAULT: HomePlanCategory = "prospect";

export type PutHomePlanFields = Partial<
  Pick<
    ClientHomePlan,
    | "optionChoices"
    | "lotId"
    | "externalId"
    | "planName"
    | "notes"
    | "customerInfo"
    | "customerCustomFields"
    | "communityPlanId"
    | "salesStaffId"
    | "category"
    | "source"
    | "lockedAt"
    | "detachedAt"
    | "totalPriceOverride"
  > & {
    discount: HomePlanDiscountFields;
    unlockUnitScope: "matched" | "all" | "none";
  }
>;

export async function putHomePlan(
  builderId: string,
  homePlanId: string,
  {
    externalId,
    optionChoices,
    notes,
    planName,
    lotId,
    customerInfo,
    customerCustomFields,
    communityPlanId,
    salesStaffId,
    category,
    source,
    lockedAt,
    discount,
    totalPriceOverride,
    unlockUnitScope,
  }: PutHomePlanFields,
): StorageResponse<ClientHomePlan> {
  try {
    const res = await putWithTimeout(
      `${WEB_API_BASE_PATH}/builders/${builderId}/home-plans/${homePlanId}`,
      {
        externalId,
        optionChoices,
        notes,
        planName,
        lotId,
        customerInfo,
        customerCustomFields,
        communityPlanId,
        salesStaffId,
        category,
        source,
        lockedAt,
        discount,
        totalPriceOverride,
        unlockUnitScope,
      },
      LAMDA_REQUEST_TIMEOUT,
    );

    const json = await res.json();

    if (!res.ok) {
      throw new Error(json);
    }

    return [json, undefined];
  } catch (e) {
    return [, await handleFetchError(e)];
  }
}

export async function moveHomePlan({
  builderId,
  homePlanId,
  moveParams,
}: {
  builderId: string;
  homePlanId: string;
  moveParams: HomePlanSchemaMove;
}): StorageResponse<ClientHomePlan> {
  try {
    const res = await putWithTimeout(
      `${WEB_API_BASE_PATH}/builders/${builderId}/home-plans/${homePlanId}/move`,
      moveParams,
    );

    const json = await res.json();

    if (!res.ok) {
      throw new Error(json);
    }

    return [json, undefined];
  } catch (e) {
    return [, await handleFetchError(e)];
  }
}

export async function putHomePlanChangeOwner(
  builderId: string,
  homePlanId: string,
  newOwnerId: string,
): StorageResponse<ClientHomePlanHydrated> {
  try {
    const res = await putWithTimeout(`${WEB_API_BASE_PATH}/builders/${builderId}/home-plans/${homePlanId}/owner`, {
      newOwnerId,
    });

    const json = await res.json();

    if (!res.ok) {
      throw new Error(json);
    }

    return [json, undefined];
  } catch (e) {
    return [, await handleFetchError(e)];
  }
}

export async function deleteHomePlan(builderId: string, homePlanId: string): StorageResponse<string> {
  try {
    const res = await deleteWithTimeout(`${WEB_API_BASE_PATH}/builders/${builderId}/home-plans/${homePlanId}`);

    if (!res.ok) {
      throw new Error(`Failed to delete home plan ${homePlanId}`);
    }

    return [homePlanId, undefined];
  } catch (e) {
    return [, await handleFetchError(e)];
  }
}

export async function putHomePlanLockedAssets(
  builderId: string,
  homePlanId: string,
  lockedAssets: HomePlanLockedAssets,
): StorageResponse<ClientHomePlan> {
  try {
    const res = await putWithTimeout(`${WEB_API_BASE_PATH}/builders/${builderId}/home-plans/${homePlanId}/assets`, {
      lockedAssets,
    });

    const json = await res.json();

    if (!res.ok) {
      throw new Error(json);
    }

    return [json, undefined];
  } catch (e) {
    return [, await handleFetchError(e)];
  }
}

export async function putHomePlanChangeStatus(
  builderId: string,
  homePlanId: string,
  planStatus: HomePlanStatus,
  shouldCharge: boolean,
  locked?: boolean,
): StorageResponse<ClientHomePlanStatusUpdateResponse> {
  try {
    const res = await putWithTimeout(`${WEB_API_BASE_PATH}/builders/${builderId}/home-plans/${homePlanId}/status`, {
      planStatus,
      shouldCharge,
      locked,
    });

    const json = await res.json();

    if (!res.ok) {
      throw new Error(json);
    }

    return [json, undefined];
  } catch (e) {
    return [, await handleFetchError(e)];
  }
}

export async function putHomePlanCustomerApproves(
  builderId: string,
  homePlanId: string,
): StorageResponse<ClientHomePlanHydrated> {
  try {
    const res = await putWithTimeout(`${WEB_API_BASE_PATH}/builders/${builderId}/home-plans/${homePlanId}/approve`);

    const json = await res.json();

    if (!res.ok) {
      throw new Error(json);
    }

    return [json, undefined];
  } catch (e) {
    return [, await handleFetchError(e)];
  }
}

export const downloadHomePlanCSV = async (
  builderId: string,
  homePlanId: string,
  homePlanSetId: string,
): Promise<UserNotificationType | undefined> => {
  try {
    const res = await getWithTimeout(`${WEB_API_BASE_PATH}/builders/${builderId}/home-plans/${homePlanId}/download`);
    const text = await res.text();

    if (!res.ok) {
      throw text;
    }

    downloadFile(`${homePlanSetId}.csv`, text, "text/csv");

    return undefined;
  } catch (e) {
    return await handleFetchError(e);
  }
};

export async function detachHomePlanChain(
  builderId: string,
  homePlanId: string,
): StorageResponse<ClientHomePlanHydratedNoDoc> {
  try {
    const res = await putWithTimeout(`${WEB_API_BASE_PATH}/builders/${builderId}/home-plans/${homePlanId}/detach`);

    const json = await res.json();

    if (!res.ok) {
      return [, await handleFetchError(new Error(json))];
    }

    return [json, undefined];
  } catch (error) {
    console.error(error);
    return [, await handleFetchError(error)];
  }
}

export async function refreshHomePlanOptions(
  builderId: string,
  homePlanId: string,
): StorageResponse<ClientHomePlanHydratedNoDoc> {
  try {
    const res = await putWithTimeout(
      `${WEB_API_BASE_PATH}/builders/${builderId}/home-plans/${homePlanId}/refresh-options`,
    );

    const json = await res.json();

    if (!res.ok) {
      return [, await handleFetchError(new Error(json))];
    }

    return [json, undefined];
  } catch (error) {
    console.error(error);
    return [, await handleFetchError(error)];
  }
}

export async function releaseHomePlanOptions(
  builderId: string,
  homePlanId: string,
): StorageResponse<ClientHomePlanHydratedNoDoc> {
  try {
    const res = await putWithTimeout(
      `${WEB_API_BASE_PATH}/builders/${builderId}/home-plans/${homePlanId}/release-options`,
    );

    const json = await res.json();

    if (!res.ok) {
      return [, await handleFetchError(new Error(json))];
    }

    return [json, undefined];
  } catch (error) {
    console.error(error);
    return [, await handleFetchError(error)];
  }
}

export async function triggerHomePlanWebhooks(
  builderId: string,
  homePlanId: string,
): StorageResponse<ClientHomePlanHydrated> {
  try {
    const res = await postWithTimeout(`${WEB_API_BASE_PATH}/builders/${builderId}/home-plans/${homePlanId}/webhooks`);
    const json = await res.json();
    if (!res.ok) {
      throw new Error(json);
    }
    return [json, undefined];
  } catch (error) {
    console.error(error);
    return [, await handleFetchError(error)];
  }
}
